import { create } from 'zustand';
import { picklistService } from '../services';
import type { ItemPendingQuantityDto } from '../types';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';

const DEBOUNCE_MS = 1500; // 1.5 seconds (matches stock store)
const STALE_TIME_MS = 2 * 60 * 1000; // 2 minutes (shorter than stock - pending data changes more often)

interface PendingQuantityCacheEntry {
  data: ItemPendingQuantityDto;
  fetchedAt: number;
}

interface PendingQuantityState {
  cache: Record<string, PendingQuantityCacheEntry>;
  loadingItems: Set<number>;
  errorItems: Record<string, string>;

  // Internal: pending items to fetch (for debounce batching)
  _pendingItemIds: Set<number>;
  _debounceTimer: ReturnType<typeof setTimeout> | null;

  // Actions
  queueFetch: (itemIds: number[]) => void;
  fetchBatch: (itemIds: number[]) => Promise<void>;
  refreshAll: (itemIds: number[]) => Promise<void>;
  clearCache: () => void;

  // Selectors
  getPendingQuantity: (netsuiteItemId: number) => ItemPendingQuantityDto | null;
  isLoading: (netsuiteItemId: number) => boolean;
}

export const usePendingQuantityStore = create<PendingQuantityState>()((set, get) => ({
  cache: {},
  loadingItems: new Set(),
  errorItems: {},
  _pendingItemIds: new Set(),
  _debounceTimer: null,

  /**
   * Queue items for pending quantity fetch with debounce batching.
   */
  queueFetch: (itemIds: number[]) => {
    const state = get();
    const now = Date.now();

    const needsFetch = itemIds.filter((id) => {
      if (state.loadingItems.has(id)) return false;
      if (state._pendingItemIds.has(id)) return false;

      const cached = state.cache[String(id)];
      if (cached && now - cached.fetchedAt < STALE_TIME_MS) {
        return false;
      }

      return true;
    });

    if (needsFetch.length === 0) return;

    const newPending = new Set(state._pendingItemIds);
    needsFetch.forEach((id) => newPending.add(id));

    if (state._debounceTimer) {
      clearTimeout(state._debounceTimer);
    }

    const timer = setTimeout(() => {
      const currentState = get();
      const idsToFetch = Array.from(currentState._pendingItemIds);

      set({ _pendingItemIds: new Set(), _debounceTimer: null });

      if (idsToFetch.length > 0) {
        currentState.fetchBatch(idsToFetch);
      }
    }, DEBOUNCE_MS);

    set({ _pendingItemIds: newPending, _debounceTimer: timer });
  },

  /**
   * Fetch pending quantities for a batch of items.
   */
  fetchBatch: async (itemIds: number[]) => {
    if (itemIds.length === 0) return;

    const state = get();

    const newLoading = new Set(state.loadingItems);
    itemIds.forEach((id) => newLoading.add(id));

    const newErrors = { ...state.errorItems };
    itemIds.forEach((id) => delete newErrors[String(id)]);

    set({ loadingItems: newLoading, errorItems: newErrors });

    try {
      const results = await picklistService.getPendingQuantities(itemIds);

      const newCache = { ...get().cache };
      const now = Date.now();

      results.forEach((item) => {
        newCache[String(item.netsuiteItemId)] = {
          data: item,
          fetchedAt: now,
        };
      });

      // Items not in results have zero pending quantities
      const returnedIds = new Set(results.map((r) => r.netsuiteItemId));
      itemIds.forEach((id) => {
        if (!returnedIds.has(id)) {
          newCache[String(id)] = {
            data: {
              netsuiteItemId: id,
              pendingPickingQuantity: 0,
              pendingPostingQuantity: 0,
              totalCommittedQuantity: 0,
            },
            fetchedAt: now,
          };
        }
      });

      const updatedLoading = new Set(get().loadingItems);
      itemIds.forEach((id) => updatedLoading.delete(id));

      set({ cache: newCache, loadingItems: updatedLoading });
    } catch (error) {
      const errorMsg = getApiErrorMessage(error, 'Failed to fetch pending quantities');

      const errorItems = { ...get().errorItems };
      itemIds.forEach((id) => {
        errorItems[String(id)] = errorMsg;
      });

      const updatedLoading = new Set(get().loadingItems);
      itemIds.forEach((id) => updatedLoading.delete(id));

      set({ errorItems, loadingItems: updatedLoading });
    }
  },

  /**
   * Force refresh pending quantities (clears cache first).
   */
  refreshAll: async (itemIds: number[]) => {
    const newCache = { ...get().cache };
    itemIds.forEach((id) => delete newCache[String(id)]);
    set({ cache: newCache });

    return get().fetchBatch(itemIds);
  },

  clearCache: () => {
    set({ cache: {}, errorItems: {}, loadingItems: new Set() });
  },

  // Selectors
  getPendingQuantity: (netsuiteItemId: number): ItemPendingQuantityDto | null => {
    return get().cache[String(netsuiteItemId)]?.data ?? null;
  },

  isLoading: (netsuiteItemId: number): boolean => {
    const state = get();
    return state.loadingItems.has(netsuiteItemId) || state._pendingItemIds.has(netsuiteItemId);
  },
}));
